import { INQUIRY_MESSAGE_MAX_LENGTH } from './inquiryPackageModel';

// Same window as the inquiry return path. localStorage survives the new tab
// opened by the verification email; sessionStorage does not.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const storage = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

export const inquiryDraftKey = listingId => `lt-inquiry-draft:${listingId}`;

const toIso = value => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
};

const fromIso = value => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
};

const serializeDateRange = range => {
  if (!range || typeof range !== 'object') {
    return null;
  }
  const startDate = toIso(range.startDate);
  const endDate = toIso(range.endDate);
  const startLodgingDate = toIso(range.startLodgingDate);
  if (!startDate && !endDate && !startLodgingDate) {
    return null;
  }
  return {
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    ...(startLodgingDate ? { startLodgingDate } : {}),
  };
};

const reviveDateRange = range => {
  if (!range || typeof range !== 'object') {
    return null;
  }
  const startDate = fromIso(range.startDate);
  const endDate = fromIso(range.endDate);
  const startLodgingDate = fromIso(range.startLodgingDate);
  if (!startDate && !endDate && !startLodgingDate) {
    return null;
  }
  return {
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    ...(startLodgingDate ? { startLodgingDate } : {}),
  };
};

const trimPackage = selectedPackage => {
  if (!selectedPackage || !selectedPackage.id) {
    return null;
  }
  return {
    id: selectedPackage.id,
    title: selectedPackage.title,
    activity: selectedPackage.activity,
    price: selectedPackage.price,
    guests: selectedPackage.guests,
    flexiblePricing: selectedPackage.flexiblePricing,
    hasLodging: selectedPackage.hasLodging,
    lodgingPrice: selectedPackage.lodgingPrice,
    species: selectedPackage.species,
  };
};

export const serializeInquiryDraft = values => {
  if (!values) {
    return null;
  }
  const message =
    typeof values.message === 'string' ? values.message.slice(0, INQUIRY_MESSAGE_MAX_LENGTH) : '';
  return {
    entry: values.entry || 'package',
    activity: values.activity || null,
    message,
    bookingDates: serializeDateRange(values.bookingDates),
    dates: serializeDateRange(values.dates),
    partySize: values.partySize == null ? null : values.partySize,
    lodgingAddons: Array.isArray(values.lodgingAddons) ? values.lodgingAddons : [],
    species: values.species || null,
    selectedPackage: trimPackage(values.selectedPackage),
  };
};

export const readInquiryDraft = (listingId, now = Date.now()) => {
  const store = storage();
  if (!store || !listingId) {
    return null;
  }
  const key = inquiryDraftKey(listingId);
  try {
    const raw = store.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed && parsed.savedAt);
    if (!Number.isFinite(savedAt) || now - savedAt > MAX_AGE_MS) {
      store.removeItem(key);
      return null;
    }
    const draft = { ...parsed };
    delete draft.savedAt;
    return {
      ...draft,
      bookingDates: reviveDateRange(draft.bookingDates),
      dates: reviveDateRange(draft.dates),
    };
  } catch (error) {
    return null;
  }
};

export const writeInquiryDraft = (listingId, values) => {
  const store = storage();
  if (!store || !listingId || !values) {
    return;
  }
  const draft = serializeInquiryDraft(values);
  if (!draft.selectedPackage && !draft.message && !draft.bookingDates) {
    return;
  }
  try {
    store.setItem(inquiryDraftKey(listingId), JSON.stringify({ ...draft, savedAt: Date.now() }));
  } catch (error) {
    // Quota or privacy mode. The in-memory form still holds the trip.
  }
};

export const clearInquiryDraft = listingId => {
  const store = storage();
  if (!store || !listingId) {
    return;
  }
  try {
    store.removeItem(inquiryDraftKey(listingId));
  } catch (error) {
    // Ignore storage failures while leaving the page.
  }
};

export const mergeInquiryDraft = (locationState, draft) => {
  const location = locationState || {};
  if (!draft) {
    return location;
  }

  const locationPackageId = location.selectedPackage?.id;
  const draftPackageId = draft.selectedPackage?.id;
  if (locationPackageId && draftPackageId && locationPackageId !== draftPackageId) {
    return location;
  }

  const locationHasDates = Boolean(location.bookingDates?.startDate);
  return {
    ...draft,
    ...location,
    message: location.message || draft.message || '',
    bookingDates: locationHasDates ? location.bookingDates : draft.bookingDates,
    dates: location.dates?.startDate ? location.dates : draft.dates,
    partySize: location.partySize == null ? draft.partySize : location.partySize,
    lodgingAddons: location.lodgingAddons || draft.lodgingAddons,
    species: location.species || draft.species,
    activity: location.activity || draft.activity,
    selectedPackage: location.selectedPackage || draft.selectedPackage,
    entry: location.entry || draft.entry || 'package',
  };
};
